"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";
import { IFooter } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {
  resolveI18nText,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";

// minimal light legal strip (copyright left, Impressum/Datenschutz right)
export default function FooterLight({
  data,
  className,
}: {
  data: IFooter;
  className?: string;
}) {
  const pathname = usePathname();

  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  if (!data) return null;

  return (
    <footer className={cn("relative w-full", className)}>
      <DecorationLine className="top-0" orientation="horizontal" style="dark" />

      <div className="mx-auto flex w-[min(165rem,100%)] flex-col-reverse items-center justify-between gap-[2rem] px-[2rem] pt-[2.25rem] pb-[4rem] lg:flex-row lg:pb-[2rem]">
        <p className="mb-0 font-geistmono text-[1.75rem] font-[400] leading-[1.5] text-[#0A1A1D]/70">
          ©{new Date().getFullYear()} Trademania LLC. All rights reserved.
        </p>

        <div className="flex items-center gap-[2rem] lg:gap-[2rem]">
          {data?.impressumLink && (
            <Link
              className="px-[1rem] py-[1rem] font-geistmono text-[1.75rem] font-[400] uppercase leading-[1.5] text-[#0A1A1D]/70 transition-opacity hover:underline hover:opacity-80"
              href={resolvePageLink(data?.impressumLink, language) ?? "/"}
              target={data.impressumLink?.openInNewTab ? "_blank" : "_self"}
            >
              <span>
                {resolveI18nText(
                  data?.impressumTitle,
                  data?.impressumTitleI18n,
                  language,
                )}
              </span>
            </Link>
          )}

          {data?.datenschutzLink && (
            <Link
              className="px-[1rem] py-[1rem] font-geistmono text-[1.75rem] font-[400] uppercase leading-[1.5] text-[#0A1A1D]/70 transition-opacity hover:underline hover:opacity-80"
              href={resolvePageLink(data?.datenschutzLink, language) ?? "/"}
              target={data.datenschutzLink?.openInNewTab ? "_blank" : "_self"}
            >
              <span>
                {resolveI18nText(
                  data?.datenschutzTitle,
                  data?.datenschutzTitleI18n,
                  language,
                )}
              </span>
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
}
